import React, { useRef } from "react";
import styled, { css } from "styled-components";
import { useDispatch } from "react-redux";

// components
import Popup from "components/ui/Popup";
import Toast from "components/ui/Toast";
import { Avatar, Button } from "components/common";

// redux
import { handleRespond } from "redux/slices/link/linkSlice";

// utils
import { calculateAge, convertAppLinkStatus, convertGender } from "utils";
import { specialFormatDate } from "utils/date";

const AppLinkRequestPopup = ({
  filter,
  visible,
  onClose,
  requestInfo,
  showBranchSelect,
}) => {
  const dispatch = useDispatch();
  const accepted = requestInfo?.intlReqCd === "A";
  const ref = useRef(null);

  const handleAccept = () => {
    dispatch(
      handleRespond({
        intlReqDtlId: requestInfo.intlReqDtlId,
        seqMember: requestInfo.seqMember,
        interlockType: "A",
        filter,
      })
    );
    ref.current.close();
    setTimeout(() => {
      Toast(
        `${requestInfo?.name}님 연동이 완료되었어요`,
        "check",
        showBranchSelect ? "120px" : "15px"
      );
    }, 500);
  };

  const handleReject = () => {
    dispatch(
      handleRespond({
        intlReqDtlId: requestInfo.intlReqDtlId,
        seqMember: requestInfo.seqMember,
        interlockType: "D",
        filter,
      })
    );
    ref.current.close();
    setTimeout(() => {
      Toast(
        `${requestInfo?.name}님 연동이 취소되었어요`,
        "close",
        showBranchSelect ? "120px" : "15px"
      );
    }, 500);
  };

  return (
    <Popup
      open={visible}
      onClose={onClose}
      ref={ref}
      padding="24px 20px 24px 20px"
    >
      <Wrapper>
        <Fieldset>
          <legend align="center">신청 회원</legend>
          <Content>
            <Avatar size={48} src={requestInfo?.memberImageUrl} />
            <div data-clarity-mask="True">
              <p className="name">{requestInfo?.name}</p>
              <p className="info">
                {requestInfo?.mobile} <span>&middot;</span>
                {requestInfo?.partnerBranchName}
              </p>
            </div>
          </Content>
        </Fieldset>

        <Fieldset colored={true} accepted={accepted}>
          <legend align="center">매칭 회원</legend>
          <Content>
            <Avatar size={48} src={requestInfo?.memberImageUrl} />
            <div data-clarity-mask="True">
              <p className="name">{requestInfo?.mobileName}</p>
              <p className="info">
                {requestInfo?.memberMobile} <span>&middot;</span>
                {convertGender(requestInfo?.mobileSex)} <span>&middot;</span>
                {`${calculateAge(requestInfo?.mobileBirthYear)}세`}
              </p>
            </div>
          </Content>
        </Fieldset>

        <AdditionalInfo accepted={accepted}>
          <div>
            <p className="title">상태</p>
            <p className="status">
              {convertAppLinkStatus(requestInfo?.intlReqCd)}
            </p>
          </div>
          <div>
            <p className="title">신청일시</p>
            <p className="content">{specialFormatDate(requestInfo?.regDts)}</p>
          </div>
          <div>
            <p className="title">처리일시</p>
            <p className="content">{specialFormatDate(requestInfo?.updDts)}</p>
          </div>
        </AdditionalInfo>

        <ButtonsWrapper>
          <Button
            variant="contained"
            className="square"
            padding="16px"
            backgroundColor="#EEF5FF"
            color="#003FBD"
            text="확인"
            onClick={() => ref.current.close()}
          />
          <Button
            variant="contained"
            className="square"
            padding="16px"
            backgroundColor={accepted ? "#DA2E2E" : "#003FBD"}
            color="#FFFFFF"
            text={accepted ? "연동취소" : "연동승인"}
            onClick={accepted ? handleReject : handleAccept}
          />
        </ButtonsWrapper>
      </Wrapper>
    </Popup>
  );
};

const Wrapper = styled.div`
  background-color: white;
  width: 100%;

  span {
    content: "·";
    margin-right: 4px;
    margin-left: 4px;
  }
`;

const Fieldset = styled.fieldset`
  width: 100%;
  height: 88px;
  border: 2px solid #d4d9e3;
  border-radius: 16px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 24px;

  legend {
    padding: 0px 8px;
    margin: auto;
    margin-bottom: -8px;
    padding-top: 0;
    padding-bottom: 0;
    font-size: 12px;
    line-height: 16px;
    color: #70747d;
    font-weight: 500;
  }

  ${(props) =>
    props.colored
      ? css`
          border-color: ${(props) => (props.accepted ? "#004FEC" : "#DA2E2E")};
          legend {
            color: ${(props) => (props.accepted ? "#004FEC" : "#DA2E2E")};
          }
        `
      : ""}
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;

  img {
    margin-right: 16px;
  }

  div {
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;
  }

  p {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  p.name {
    font-weight: bold;
    font-size: 16px;
    line-height: 24px;
    color: #26282b;
    margin-bottom: 4px;
  }

  p.info {
    font-size: 14px;
    line-height: 20px;
    color: #26282b;
    font-feature-settings: "tnum" on, "lnum" on;
  }
`;

const AdditionalInfo = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;

  div {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
  div:not(:last-child) {
    margin-bottom: 24px;
  }

  p.title {
    color: #70747d;
  }

  p.status {
    font-weight: 600;
    color: ${(props) => (props.accepted ? "#004FEC" : "#DA2E2E")};
  }

  p.content {
    color: #26282b;
  }
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  margin-top: 44px;
  button:first-child {
    margin-right: 12px;
  }
`;

export default AppLinkRequestPopup;
